/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React, { type ReactNode } from 'react';
import { useHistory } from 'react-router';

import { ListeTable, ListeTableContainer } from '@mairistem/liste';

import { baseUrlApp } from '@/routes';
import ActeMariageListeTableRow from './ActeMariageListeTableRow';

interface ActeMariageListeTableProps {
  items?: Array<any>;
  count?: number;
  perPage?: number;
  loading?: boolean;
  onLoad: any;
  onSort: any;
  /** Réduit la liste à la seule colonne du nom, pour la sidebar de la fiche. */
  sidebar?: boolean;
}

/**
 * Les sept colonnes de la liste des mariages.
 *
 * Seule liste à afficher DEUX personnes principales, d'où les deux colonnes de
 * conjoint. Les parents n'y figurent pas : la maquette ne les demande pas, et
 * quatre colonnes de plus rendaient la table illisible sans défilement.
 *
 * ⚠️ Les `width` doivent totaliser 16, comme sur les trois autres listes. Les
 * cinq premières reprennent celles de la liste des naissances, pour que les
 * quatre écrans s'alignent à l'œil ; les deux conjoints se partagent le reste à
 * égalité — deux colonnes symétriques ne doivent pas avoir deux largeurs.
 */
const ActeMariageListeTable = (props: ActeMariageListeTableProps) => {
  const { items, count, perPage, loading, onLoad, onSort, sidebar } = props;

  const history = useHistory();

  const renderRow = (item: any) => (
    <ActeMariageListeTableRow item={item} sidebar={sidebar} />
  );

  const handleSelect = (selectedItem: any) => {
    const identifiant = selectedItem?.identifiant ?? selectedItem?.id;
    if (identifiant != null) {
      history.push(`${baseUrlApp}mariage/${identifiant}`);
    }
  };

  const listItems = React.useMemo(() => {
    const list = items || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (list as any[]).map((item) => {
      if (item?.identifiant == null && item?.id != null) {
        return { ...item, identifiant: item.id };
      }
      return item;
    });
  }, [items]);

  return (
    <ListeTableContainer
      // @ts-ignore
      items={listItems}
    >
      <ListeTable
        items={listItems}
        loading={loading}
        perPage={perPage}
        count={count}
        onLoad={onLoad}
        onSort={onSort}
        onSelect={handleSelect}
      >
        {/* Colonnes en enfants DIRECTS du Header : cf. MentionListeTable. */}
        {sidebar ? (
          <ListeTable.Header>
            <ListeTable.Column name="nom" width={16}>
              Conjoints
            </ListeTable.Column>
          </ListeTable.Header>
        ) : (
          <ListeTable.Header>
            <ListeTable.Column name="registre" width={3}>
              Registre
            </ListeTable.Column>
            <ListeTable.Column name="typeActe" width={2}>
              Type d&apos;acte
            </ListeTable.Column>
            <ListeTable.Column name="numero" sortable width={2}>
              N° acte et ordre
            </ListeTable.Column>
            <ListeTable.Column name="dresse" sortable width={2}>
              Dressé
            </ListeTable.Column>
            <ListeTable.Column name="dateMariage" sortable width={1}>
              Marié le
            </ListeTable.Column>
            <ListeTable.Column name="conjoint1" width={3}>
              1er conjoint
            </ListeTable.Column>
            <ListeTable.Column name="conjoint2" width={3}>
              2ème conjoint
            </ListeTable.Column>
          </ListeTable.Header>
        )}
        {renderRow as unknown as ReactNode}
      </ListeTable>
    </ListeTableContainer>
  );
};

export default ActeMariageListeTable;
